import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip } from "./chip";
import { EmptyState } from "./item-ui";
import { useQualityHistogram, type HistogramRow } from "./quality";

const DAYS = 30;

/** Left to right: a score of 0, 1, 2, 3. The same four colours as the pips on the score card. */
const SEGMENTS: { bar: string; text: string; swatch: string }[] = [
  { bar: "bg-red-500", text: "text-white", swatch: "bg-red-500" },
  { bar: "bg-orange-400", text: "text-black", swatch: "bg-orange-400" },
  { bar: "bg-amber-300", text: "text-black", swatch: "bg-amber-300" },
  { bar: "bg-emerald-500", text: "text-black", swatch: "bg-emerald-500" },
];

function barLabel(row: HistogramRow): string {
  const [a, b, c, d] = row.counts;
  return `${row.label}: ${a} scored 0, ${b} scored 1, ${c} scored 2, ${d} scored 3`;
}

/** One stacked bar: the share of pieces that scored 0, 1, 2 and 3 on a check. */
function StackedBar({ row }: { row: HistogramRow }) {
  if (row.total === 0) {
    return <div className="h-6 w-full rounded-md border border-dashed border-border" role="img" aria-label={`${row.label}: no scores yet`} />;
  }
  return (
    <div className="flex h-6 w-full overflow-hidden rounded-md bg-muted/40" role="img" aria-label={barLabel(row)} data-testid={`bar-${row.id}`}>
      {row.counts.map((n, i) =>
        n > 0 ? (
          <div
            key={i}
            className={`flex items-center justify-center text-[10px] font-bold ${SEGMENTS[i].bar} ${SEGMENTS[i].text}`}
            style={{ width: `${(n / row.total) * 100}%` }}
            title={`${n} scored ${i}`}
            data-testid={`bar-${row.id}-${i}`}
          >
            {n / row.total >= 0.08 ? n : ""}
          </div>
        ) : null,
      )}
    </div>
  );
}

function CheckRow({ row }: { row: HistogramRow }) {
  return (
    <li
      className={`rounded-xl border p-3 ${row.saturated && row.sampleSufficient ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-border bg-card/60"}`}
      data-testid={`quality-row-${row.id}`}
      data-saturated={row.saturated && row.sampleSufficient}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="min-w-0 text-sm font-medium text-foreground" title={row.help || undefined}>{row.label}</p>
        {row.saturated && row.sampleSufficient && (
          <Chip
            className="border-amber-500/50 bg-amber-500/15 text-amber-200"
            title="Nearly every recent piece scored 3 on this check, so it no longer separates strong pieces from weak ones."
          >
            <AlertTriangle size={11} className="mr-1" aria-hidden /> no longer discriminating
          </Chip>
        )}
        {row.saturated && !row.sampleSufficient && (
          <Chip className="border-border bg-muted/40 text-muted-foreground" title="Every piece graded so far scored 3, but there are too few to say the check has stopped discriminating.">
            all 3s so far, too few to judge
          </Chip>
        )}
      </div>
      <div className="mt-2">
        <StackedBar row={row} />
      </div>
      <p className="mt-1.5 text-xs tabular-nums text-muted-foreground" data-testid={`quality-counts-${row.id}`}>
        {row.total === 0 ? (
          "No scores yet"
        ) : (
          <>
            0: {row.counts[0]} · 1: {row.counts[1]} · 2: {row.counts[2]} · 3: {row.counts[3]} <span className="opacity-70">({row.total} scored)</span>
          </>
        )}
      </p>
    </li>
  );
}

/**
 * Quality: how the ten rubric checks have scored across recent pieces. The point of a histogram over an average is the
 * saturation badge: a check that nearly everything scores 3 on has stopped telling strong pieces from weak ones.
 */
export function QualityTab() {
  const { data, available, isLoading, isError, refetch } = useQualityHistogram(DAYS);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  // Only a check with enough graded pieces behind it is called saturated; a few early 3s are not a verdict.
  const saturated = data.rows.filter((r) => r.saturated && r.sampleSufficient);
  const early = data.rows.filter((r) => r.saturated && !r.sampleSufficient);
  const rule =
    data.threshold !== null || data.minSample !== null
      ? `A check is flagged when ${Math.round((data.threshold ?? 0.9) * 100)}% or more of at least ${data.minSample ?? 20} graded pieces score 3.`
      : null;

  return (
    <div className="space-y-5" data-testid="quality-tab">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Quality</h2>
        <p className="text-sm text-muted-foreground" data-testid="quality-explainer">
          Scores are graded 0–3; a check that is always 3 has stopped telling us anything.
        </p>
        {rule && <p className="mt-0.5 text-xs text-muted-foreground" data-testid="quality-rule">{rule}</p>}
      </div>

      {isError ? (
        <div className="space-y-3 py-12 text-center" data-testid="quality-error">
          <p className="text-sm text-foreground">Couldn't load the quality history.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
        </div>
      ) : !available ? (
        <EmptyState title="Quality history isn't available on this server yet.">Scores will show here once it is.</EmptyState>
      ) : !data.hasData ? (
        <EmptyState title={`No scored pieces in the last ${DAYS} days.`}>A piece is scored when its independent review finishes.</EmptyState>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border bg-card/50 px-4 py-3" data-testid="quality-summary">
            <p className="text-sm text-foreground">
              <span className="font-semibold" data-testid="quality-items">{data.items}</span> published {data.items === 1 ? "piece" : "pieces"} scored in the last {DAYS} days
            </p>
            <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" aria-label="Score colours" data-testid="quality-legend">
              {SEGMENTS.map((s, i) => (
                <li key={i} className="inline-flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-sm ${s.swatch}`} aria-hidden /> {i}
                  {i === 3 ? " (best)" : ""}
                </li>
              ))}
            </ul>
          </div>

          {saturated.length > 0 ? (
            <p className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100" role="status" data-testid="quality-saturated-note">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                {saturated.length} of {data.rows.length} checks no longer discriminating: {saturated.map((r) => r.label).join(", ")}. Nearly every recent piece scores 3 on them, so they are worth tightening or replacing.
              </span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="quality-none-saturated">No check is flagged as stuck at 3.</p>
          )}
          {early.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="quality-early-note">
              {early.map((r) => r.label).join(", ")} {early.length === 1 ? "has" : "have"} scored 3 on nearly every piece so far, but too few pieces are graded to call {early.length === 1 ? "it" : "them"} yet.
            </p>
          )}

          <ul className="grid gap-3 md:grid-cols-2" data-testid="quality-rows">
            {data.rows.map((r) => <CheckRow key={r.id} row={r} />)}
          </ul>
        </>
      )}
    </div>
  );
}
